import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Share2, Download, Copy, Check, MessageSquare, Mail, Facebook } from "lucide-react";

const enc = encodeURIComponent;

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.83l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function ShareCard({ property, publicUrl }) {
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const slug = property?.slug || "";
  const shareUrl = publicUrl.replace("/p/", "/api/share/");
  const ogUrl = publicUrl.replace("/p/", "/api/og/") + ".png";

  const address = property?.address_line || "This home";
  const city = property?.city || "";
  const title = `${address}${city ? ", " + city : ""}`;
  const shareText = `Now for sale: ${title}. Take the full tour →`;

  const shares = [
    { key: "facebook", label: "Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { key: "x", label: "X", Icon: XIcon, href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}` },
    { key: "sms", label: "Text", Icon: MessageSquare, href: `sms:?&body=${enc(shareText + " " + shareUrl)}` },
    { key: "email", label: "Email", Icon: Mail, href: `mailto:?subject=${enc(title)}&body=${enc(shareText + "\n\n" + shareUrl)}` },
  ];

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) { toast.error("Couldn't generate the QR code. Save the listing and try again."); return; }
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${slug || "listing"}-qr.png`;
    a.click();
    toast.success("QR code downloaded");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Share link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div data-testid="share-card" className="bg-white border border-[#E5E5E2] rounded-sm p-5">
      <h3 className="text-sm font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2"><Share2 className="w-4 h-4 text-[#1F3C88]" /> Share &amp; QR Code</h3>

      <div className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#8a8a86] mb-2">Link preview (Facebook, texts &amp; more)</div>
      <div className="rounded-md overflow-hidden border border-[#E5E5E2] bg-[#F0EFEC]" style={{ aspectRatio: "1200 / 630" }}>
        <img data-testid="og-preview" src={ogUrl} alt="Link preview" className="w-full h-full object-cover" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        <div ref={qrRef} data-testid="qr-code" className="border border-[#E5E5E2] rounded-md p-4 flex items-center justify-center bg-white">
          <QRCodeCanvas value={publicUrl} size={116} fgColor="#0F172A" level="M" includeMargin={false} />
        </div>
        <div className="flex flex-col justify-between">
          <p className="text-sm text-[#575755] leading-relaxed">Print this QR on open-house signs and flyers. Scanning it opens the property website.</p>
          <div className="space-y-2 mt-3">
            <button type="button" data-testid="download-qr-btn" onClick={downloadQR} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#152A61] transition-colors"><Download className="w-4 h-4" /> Download QR</button>
            <button type="button" data-testid="share-copy-link" onClick={copyLink} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-[#E5E5E2] text-[#0A0A0A] text-sm font-semibold hover:bg-[#F0EFEC] transition-colors">{copied ? <Check className="w-4 h-4 text-[#1F8A4C]" /> : <Copy className="w-4 h-4" />} Copy share link</button>
          </div>
        </div>
      </div>

      <div className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#8a8a86] mt-6 mb-2">Share to</div>
      <div className="grid grid-cols-4 gap-2">
        {shares.map(({ key, label, Icon, href }) => (
          <a key={key} data-testid={`share-${key}`} href={href} target="_blank" rel="noreferrer"
             className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md border border-[#E5E5E2] text-[#575755] hover:border-[#1F3C88] hover:text-[#1F3C88] transition-colors">
            <Icon className="w-5 h-5" />
            <span className="text-xs font-medium">{label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
